/* eslint-env jest */
import AbsolutePosImgs from './AbsolutePosImgs';
import { LAYOUTS } from './packageLayouts';

// AbsolutePosImgs is called as a plain function returning a React element tree,
// so we can invoke it directly and inspect the output without a DOM renderer.
// We mock the data hooks (and the asset imports) to feed it known poses and
// backgrounds, and stub Math.random so the "random vertical" pose picks are
// deterministic.

jest.mock('./Watermark_v2.png', () => 'watermark.png', { virtual: true });
jest.mock('./Horiz_Grad_Watermark.png', () => 'horiz-watermark.png', { virtual: true });
jest.mock('./AbsolutePosImgs.scss', () => ({}), { virtual: true });

let mockPosesData;
let mockBackgroundsData;
let mockPricelistData;
let mockOrientations; // url -> 'PORTRAIT' | 'LANDSCAPE' | null (null = still loading); default PORTRAIT
let mockPackagePosesData;
let mockPackagePosesPending;
let mockBackgroundsPending;

jest.mock('../../../hooks/usePoses', () => () => ({ data: mockPosesData }), { virtual: true });
jest.mock('../../../hooks/useMultiSeasonPoses', () => () => ({ data: mockPosesData }), { virtual: true });
jest.mock('../../../hooks/useBackgrounds', () => () => ({
  data: mockBackgroundsData,
  isPlaceholderData: mockBackgroundsPending,
}), { virtual: true });
jest.mock('../../../hooks/usePricelist', () => () => ({ data: mockPricelistData }), { virtual: true });
jest.mock('../../../hooks/usePackagePoses', () => () => ({
  data: mockPackagePosesData,
  isPlaceholderData: mockPackagePosesPending,
}), { virtual: true });
jest.mock('../../../hooks/useImageOrientations', () => (urls) => {
  const resolved = {};
  (urls || []).filter(Boolean).forEach((url) => {
    const orientation = mockOrientations[url] === undefined ? 'PORTRAIT' : mockOrientations[url];
    if (orientation) resolved[url] = { orientation };
  });
  return resolved;
}, { virtual: true });

const customer = { currentStudent: { id: 42 } };
const poses = (urls) => urls.map((imageUrl) => ({ imageUrl }));

// Collect every <img> in the returned element tree.
const collectImgs = (node, acc = []) => {
  if (!node || typeof node !== 'object') return acc;
  if (Array.isArray(node)) {
    node.forEach((n) => collectImgs(n, acc));
    return acc;
  }
  if (node.type === 'img') acc.push(node.props);
  const children = node.props && node.props.children;
  if (children) collectImgs(children, acc);
  return acc;
};

beforeEach(() => {
  mockOrientations = {};
  mockPackagePosesData = [];
  mockPackagePosesPending = false;
  mockBackgroundsPending = false;
  mockPricelistData = { id: 1 };
  mockBackgroundsData = [
    { imageUrl: 'https://s3/Senior_Blue.png' },
    { imageUrl: 'https://s3/GrayBackdrop.png' },
    { imageUrl: 'https://s3/classroom.png' },
  ];
  mockPosesData = poses(['https://s3/p-01.png', 'https://s3/p-02.png', 'https://s3/p-03.png']);
});

describe('AbsolutePosImgs — render', () => {
  it('returns null when there are no poses', () => {
    mockPosesData = [];
    expect(AbsolutePosImgs(customer, 611, { name: 'Basic Package' })).toBeNull();
  });

  it('returns null when every pose is a horizontal .jpg', () => {
    mockPosesData = poses(['https://s3/p-01.jpg', 'https://s3/p-02.jpg']);
    expect(AbsolutePosImgs(customer, 611, { name: 'Basic Package' })).toBeNull();
  });

  it('returns null for an option with no preview layout', () => {
    expect(AbsolutePosImgs(customer, 611, { name: 'Magnets' })).toBeNull();
  });

  it('renders a pose + watermark per visible slot, plus a bg img where the slot has one', () => {
    // scholarsChoice has a background on every visible slot.
    const slots = LAYOUTS.scholarsChoice.filter(Boolean);
    const withBg = slots.filter((s) => s.background).length;
    const el = AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" });
    const imgs = collectImgs(el);
    expect(imgs).toHaveLength(slots.length * 2 + withBg);
  });

  it('resolves pose1 to the first pose and a background tag to the school URL', () => {
    const randomSpy = jest.spyOn(Math, 'random').mockReturnValue(0);
    const el = AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" });
    const srcs = collectImgs(el).map((p) => p.src);
    // scholarsChoice slot 1 is pose1 -> the student's first pose...
    expect(srcs).toContain('https://s3/p-01.png');
    // ...with the 'grayBg' tag, which resolves to the bg whose URL contains 'Gray'.
    expect(srcs).toContain('https://s3/GrayBackdrop.png');
    randomSpy.mockRestore();
  });

  it('uses the standard watermark for png poses', () => {
    jest.spyOn(Math, 'random').mockReturnValue(0);
    const el = AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" });
    const watermarks = collectImgs(el).filter((p) => p.alt === 'Watermark');
    expect(watermarks.length).toBeGreaterThan(0);
    watermarks.forEach((w) => expect(w.src).toBe('watermark.png'));
    Math.random.mockRestore();
  });

  it('falls back to the first background when no tag matches the school set', () => {
    mockBackgroundsData = [{ imageUrl: 'https://s3/OnlyOne.png' }];
    jest.spyOn(Math, 'random').mockReturnValue(0);
    const el = AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" });
    const srcs = collectImgs(el).map((p) => p.src);
    const poseSrcs = new Set(mockPosesData.map((p) => p.imageUrl));
    // Every non-pose, non-watermark img (i.e. the backgrounds) falls back to OnlyOne.
    const bgSrcs = srcs.filter((s) => !poseSrcs.has(s) && s !== 'watermark.png');
    expect(bgSrcs.length).toBeGreaterThan(0);
    expect(new Set(bgSrcs)).toEqual(new Set(['https://s3/OnlyOne.png']));
    Math.random.mockRestore();
  });
});

describe('AbsolutePosImgs — pose role selection', () => {
  it('returns null while pose orientations are still resolving', () => {
    mockOrientations = { 'https://s3/p-01.png': null };
    expect(AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" })).toBeNull();
  });

  it('keeps landscape poses (designer looks) out of the vertical slots', () => {
    mockPosesData = poses(['https://s3/p-01.png', 'https://s3/p-08.png', 'https://s3/p-09.png']);
    mockOrientations = {
      'https://s3/p-08.png': 'LANDSCAPE',
      'https://s3/p-09.png': 'LANDSCAPE',
    };
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const srcs = collectImgs(el).map((p) => p.src);
    expect(srcs).toContain('https://s3/p-01.png');
    expect(srcs).not.toContain('https://s3/p-08.png');
    expect(srcs).not.toContain('https://s3/p-09.png');
  });

  it('never uses graphic (G) renders, even portrait-oriented ones', () => {
    mockPosesData = poses(['https://s3/p-01.png', 'https://s3/p-G02.png']);
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const srcs = collectImgs(el).map((p) => p.src);
    expect(srcs).not.toContain('https://s3/p-G02.png');
  });

  it('returns null while the pricelist Poses list is still loading', () => {
    mockPackagePosesPending = true;
    expect(AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" })).toBeNull();
  });

  it("draws the pool from the pricelist's Poses list, in admin order", () => {
    mockPosesData = [
      { number: '01', imageUrl: 'https://s3/p-01.png' },
      { number: '02', imageUrl: 'https://s3/p-02.png' },
      { number: '03', imageUrl: 'https://s3/p-03.png' },
    ];
    mockPackagePosesData = [{ poseNumber: '02' }, { poseNumber: '03' }];
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const poseSrcs = collectImgs(el)
      .map((p) => p.src)
      .filter((s) => s.startsWith('https://s3/p-'));
    // 01 is in the gallery but not on the pricelist; 02 leads as pose1.
    expect(poseSrcs).not.toContain('https://s3/p-01.png');
    expect(poseSrcs).toContain('https://s3/p-02.png');
  });

  it('keeps horizontal pricelist poses out even when the pricelist lists them', () => {
    mockPosesData = [
      { number: '01', imageUrl: 'https://s3/p-01.png' },
      { number: '10', imageUrl: 'https://s3/p-10.png' },
    ];
    mockPackagePosesData = [{ poseNumber: '01' }, { poseNumber: '10' }];
    mockOrientations = { 'https://s3/p-10.png': 'LANDSCAPE' };
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const srcs = collectImgs(el).map((p) => p.src);
    expect(srcs).toContain('https://s3/p-01.png');
    expect(srcs).not.toContain('https://s3/p-10.png');
  });

  it('matches pricelist rows by season (case-insensitive) on multi-season pricelists', () => {
    mockPricelistData = { id: 1, multipleSeasons: ['fall2026', 'fall2027'] };
    mockPosesData = [
      { number: '01', folder: 'fall2026', imageUrl: 'https://s3/old-01.png' },
      { number: '01', folder: 'fall2027', imageUrl: 'https://s3/new-01.png' },
    ];
    mockPackagePosesData = [{ poseNumber: '01', season: 'Fall2027' }];
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const poseSrcs = collectImgs(el)
      .map((p) => p.src)
      .filter((s) => s.includes('-01.png'));
    expect(poseSrcs).toContain('https://s3/new-01.png');
    expect(poseSrcs).not.toContain('https://s3/old-01.png');
  });

  it('falls back to the student portraits when no pricelist pose matches', () => {
    mockPosesData = [
      { number: '01', imageUrl: 'https://s3/p-01.png' },
      { number: '02', imageUrl: 'https://s3/p-02.png' },
    ];
    mockPackagePosesData = [{ poseNumber: '55' }];
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const srcs = collectImgs(el).map((p) => p.src);
    expect(srcs).toContain('https://s3/p-01.png');
  });

  it('renders nothing when no portrait-oriented pose exists (never the raw gallery)', () => {
    mockPosesData = poses(['https://s3/p-08.png', 'https://s3/p-G02.png']);
    mockOrientations = { 'https://s3/p-08.png': 'LANDSCAPE' };
    expect(AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' })).toBeNull();
  });

  it('fails closed on UNKNOWN orientation (failed probe) poses', () => {
    mockPosesData = poses(['https://s3/p-01.png', 'https://s3/p-02.png']);
    mockOrientations = { 'https://s3/p-02.png': 'UNKNOWN' };
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const srcs = collectImgs(el).map((p) => p.src);
    expect(srcs).toContain('https://s3/p-01.png');
    expect(srcs).not.toContain('https://s3/p-02.png');
  });

  it('does not wander to the last pose for U0 jobs with no 25.jpg class photo', () => {
    // Legacy poseHoriz scanning matched the JOB code (U0/J0/E0) and, finding no
    // 25.jpg, walked to the last pose — the framed-mockup G render.
    mockPosesData = poses(['https://s3/196-U001Q-x-01.png', 'https://s3/196-U001Q-x-G02.png']);
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const srcs = collectImgs(el).map((p) => p.src);
    expect(srcs).toContain('https://s3/196-U001Q-x-01.png');
    expect(srcs).not.toContain('https://s3/196-U001Q-x-G02.png');
  });

  it('still previews the 25.jpg class photo as poseHoriz for U0 jobs', () => {
    mockPosesData = poses(['https://s3/196-U001Q-x-01.png', 'https://s3/196-U001Q-x-25.jpg']);
    mockOrientations = { 'https://s3/196-U001Q-x-25.jpg': 'LANDSCAPE' };
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const srcs = collectImgs(el).map((p) => p.src);
    expect(srcs).toContain('https://s3/196-U001Q-x-25.jpg');
  });
});

describe('AbsolutePosImgs — backgrounds and slot geometry', () => {
  it('never places a landscape (Horiz) background in a slot', () => {
    mockBackgroundsData = [
      { imageUrl: 'https://s3/Spring_Bridge_Horiz.png' },
      { imageUrl: 'https://s3/GrayBackdrop.png' },
    ];
    mockOrientations = { 'https://s3/Spring_Bridge_Horiz.png': 'LANDSCAPE' };
    jest.spyOn(Math, 'random').mockReturnValue(0);
    // scholarsChoice slots all carry background tags; none matches these names,
    // so the fallback background is used — it must be the portrait one.
    const el = AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" });
    const srcs = collectImgs(el).map((p) => p.src);
    expect(srcs).not.toContain('https://s3/Spring_Bridge_Horiz.png');
    expect(srcs).toContain('https://s3/GrayBackdrop.png');
    Math.random.mockRestore();
  });

  it('renders slots with no bg layer rather than a landscape-only background set', () => {
    mockBackgroundsData = [{ imageUrl: 'https://s3/Spring_Bridge_Horiz.png' }];
    mockOrientations = { 'https://s3/Spring_Bridge_Horiz.png': 'LANDSCAPE' };
    const el = AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" });
    const srcs = collectImgs(el).map((p) => p.src);
    expect(srcs).not.toContain('https://s3/Spring_Bridge_Horiz.png');
    expect(srcs.some((s) => s.startsWith('https://s3/p-'))).toBe(true); // poses still render
  });

  it('waits for background orientations before rendering a bg-using layout', () => {
    mockBackgroundsData = [{ imageUrl: 'https://s3/GrayBackdrop.png' }];
    mockOrientations = { 'https://s3/GrayBackdrop.png': null };
    // scholarsChoice places backgrounds, so it must wait...
    expect(AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" })).toBeNull();
    // ...but picturePerfect has no background slots and renders regardless.
    expect(AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' })).not.toBeNull();
  });

  it('waits for the backgrounds list itself only when the layout uses backgrounds', () => {
    mockBackgroundsPending = true;
    expect(AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" })).toBeNull();
    expect(AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' })).not.toBeNull();
  });

  it('bounds each slot to the pose image aspect family (4:5 portrait, 5:4 landscape)', () => {
    mockPosesData = poses(['https://s3/196-U001Q-x-01.png', 'https://s3/196-U001Q-x-25.jpg']);
    mockOrientations = { 'https://s3/196-U001Q-x-25.jpg': 'LANDSCAPE' };
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const imgs = collectImgs(el);
    expect(imgs.length).toBeGreaterThan(0);
    imgs.forEach((img) => {
      expect(['4 / 5', '5 / 4']).toContain(img.style.aspectRatio);
      expect(img.style.objectFit).toBe('contain');
    });
    // The landscape 25.jpg class photo (poseHoriz) gets the wide box; portraits the tall one.
    const horiz = imgs.filter((img) => img.src === 'https://s3/196-U001Q-x-25.jpg');
    expect(horiz.length).toBeGreaterThan(0);
    horiz.forEach((img) => expect(img.style.aspectRatio).toBe('5 / 4'));
    const portrait = imgs.filter((img) => img.src === 'https://s3/196-U001Q-x-01.png');
    portrait.forEach((img) => expect(img.style.aspectRatio).toBe('4 / 5'));
  });

  it("maps the 2026-27 'Single 8x10 Print' option name to the justAn8x10 layout", () => {
    const el = AbsolutePosImgs(customer, 584, { name: 'Single 8x10 Print' });
    expect(el).not.toBeNull();
    const poseImgs = collectImgs(el).filter((p) => p.src.startsWith('https://s3/p-'));
    expect(poseImgs.length).toBe(1); // justAn8x10 renders exactly one pose slot
  });
});
